/**
 * Holly's animation contract, and it is deliberately NOT Rosalie's.
 *
 * `types.ts` describes a 0.32 m plush piglet with rigid legs, ears that flick, a
 * snout that pitches and a signature roll. Holly is 1.24 m of sheep with a neck,
 * a head that is a separate link, ears merged into the skull, and one action the
 * whole of beat 9 turns on: she rears onto her hind legs and lifts a drop bar
 * with her nose. Four of `Pose`'s channels would be dead on her and the one she
 * actually needs — `rear` — has no equivalent on a pig.
 *
 * So this is a second, smaller contract, and the shared idea is the one that
 * matters: **every field is a DELTA from the bind pose** the mesh factory built.
 * Her head already carries 0.38 rad of nose-down (see `holly.ts`); a clip that
 * wants her looking up asks for a negative `headPitch`, never for an absolute.
 * That is what lets her be reproportioned without touching a clip, which is the
 * same reason `types.ts` gives, and it has already been paid for twice on her.
 *
 * The D-002 rule about attention holds here too: `neck*` and `head*` express
 * where she is LOOKING and nothing else. There is no mood channel and adding one
 * is how the projection mechanism dies.
 */

/** One leg, pivoting at the hip. Deltas, radians and metres. */
export interface HollyLegPose {
  /** Forward/back swing about X. Positive swings the hoof forward. */
  swing: number;
  /** Outward splay about Z. Positive pushes the hoof away from her centreline. */
  splay: number;
  /** Hoof lift, metres, straight up. Her legs have a hock but no IK. */
  lift: number;
}

export const HOLLY_NEUTRAL_LEG: Readonly<HollyLegPose> = { swing: 0, splay: 0, lift: 0 };

export interface HollyPose {
  /** Whole-body rise and fall, metres. Gait bounce and the breath both live here. */
  bob: number;
  /**
   * Ribcage swell, 1 = unchanged.
   *
   * A sheep standing still is not a statue, and on an animal with no face to act
   * with, breathing is most of what says she is alive. It is capped in the rig
   * for the same reason Rosalie's squash is: this is the channel a later clip
   * would reach for to make her "heave", and heaving is acting.
   */
  breathe: number;
  /**
   * How far her front end is up, radians, about her hind hooves. Never negative.
   *
   * **This is beat 9's whole mechanism** and it is why she is a separate rig.
   * The bar is at 1.4 m and her muzzle at rest is under 1.0; nothing she can do
   * with her neck alone reaches it. The rig rotates her about the hind hoof
   * contact rather than about her origin, so her back feet stay on the floor
   * instead of swinging through it.
   */
  rear: number;
  /** Body pitch about X, on top of `rear`. Positive tips her nose down. */
  pitch: number;
  /** Body roll about Z. Positive rolls toward her left. Weight shift, not banking. */
  roll: number;

  /** Neck bend. NEGATIVE raises her head. Attention only (D-002). */
  neckPitch: number;
  neckYaw: number;
  /** Head on the poll. NEGATIVE raises the muzzle. Attention only (D-002). */
  headPitch: number;
  headYaw: number;

  legFL: HollyLegPose;
  legFR: HollyLegPose;
  legHL: HollyLegPose;
  legHR: HollyLegPose;
}

export function neutralHollyPose(): HollyPose {
  return {
    bob: 0,
    breathe: 1,
    rear: 0,
    pitch: 0,
    roll: 0,
    neckPitch: 0,
    neckYaw: 0,
    headPitch: 0,
    headYaw: 0,
    legFL: { ...HOLLY_NEUTRAL_LEG },
    legFR: { ...HOLLY_NEUTRAL_LEG },
    legHL: { ...HOLLY_NEUTRAL_LEG },
    legHR: { ...HOLLY_NEUTRAL_LEG },
  };
}

/**
 * A clip on Holly. Same contract as `Clip`: pure function of phase, writes into
 * `out`, no internal clock and no unseeded randomness.
 */
export interface HollyClip {
  readonly id: string;
  /** Seconds for one full cycle at nominal playback rate. */
  readonly duration: number;
  readonly loop: boolean;
  sample(phase: number, out: HollyPose): void;
}

function blendLeg(a: HollyLegPose, b: HollyLegPose, t: number, out: HollyLegPose): void {
  out.swing = a.swing + (b.swing - a.swing) * t;
  out.splay = a.splay + (b.splay - a.splay) * t;
  out.lift = a.lift + (b.lift - a.lift) * t;
}

/** Linear blend of two poses into `out`. `t` = 0 gives `a`, 1 gives `b`. */
export function blendHollyPose(a: HollyPose, b: HollyPose, t: number, out: HollyPose): void {
  const m = (x: number, y: number): number => x + (y - x) * t;
  out.bob = m(a.bob, b.bob);
  out.breathe = m(a.breathe, b.breathe);
  out.rear = m(a.rear, b.rear);
  out.pitch = m(a.pitch, b.pitch);
  out.roll = m(a.roll, b.roll);
  out.neckPitch = m(a.neckPitch, b.neckPitch);
  out.neckYaw = m(a.neckYaw, b.neckYaw);
  out.headPitch = m(a.headPitch, b.headPitch);
  out.headYaw = m(a.headYaw, b.headYaw);
  blendLeg(a.legFL, b.legFL, t, out.legFL);
  blendLeg(a.legFR, b.legFR, t, out.legFR);
  blendLeg(a.legHL, b.legHL, t, out.legHL);
  blendLeg(a.legHR, b.legHR, t, out.legHR);
}
